import React from 'react';
import { Icon } from '@heartinternet/hi-ui';
import type { Allowance } from '../types';
import { upgradeState } from '../utils';
import { DropdownMenu } from '../../../components/DropdownMenu';

export const UPGRADE_PENDING_TOOLTIP =
  'Upgrading is not possible while we process your renewal payment';

export interface ManageMenuProps {
  row: Allowance;
  onChangePassword: () => void;
  onUpgrade: () => void;
}

/**
 * Row-actions "Manage" dropdown for an assigned mailbox — the portal's yellow cog trigger
 * that reveals a menu on click, matching the package-table manage control (cPanel / Manage
 * Packages). Open/close + on-screen positioning come from the shared DropdownMenu component:
 * the menu is portaled into <body> with fixed positioning so it can never be clipped by the
 * table / mobile-card containers (the off-screen mobile bug, issue #325).
 *
 * The Upgrade item tracks the four upgrade states (see utils.upgradeState):
 *   none        → already Pro: no Upgrade item
 *   upgradeable → "Upgrade to Pro £X" → opens the price-confirm modal
 *   bulk ('-1') → "Upgrade to Pro"    → opens the can't-upgrade modal
 *   pending     → "Upgrade to Pro" disabled, with the renewal-payment explanation (was the ⌛)
 */
export const ManageMenu: React.FC<ManageMenuProps> = ({ row, onChangePassword, onUpgrade }) => {
  const state = upgradeState(row);

  return (
    <DropdownMenu
      className="hi-ui-be-manage"
      menuClassName="hi-ui-be-manage__menu"
      menuRole="menu"
      renderTrigger={({ open, ref, toggle }) => (
        <button
          ref={ref}
          type="button"
          className="hi-ui-be-manage__trigger"
          aria-label="Manage mailbox"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
        >
          <Icon name="cog" size={20} />
        </button>
      )}
    >
      {({ close }) => (
        <>
          <button
            type="button"
            role="menuitem"
            className="hi-ui-be-manage__item"
            onClick={() => {
              close();
              onChangePassword();
            }}
          >
            Change password
          </button>
          {(state === 'upgradeable' || state === 'bulk') && (
            <button
              type="button"
              role="menuitem"
              className="hi-ui-be-manage__item"
              onClick={() => {
                close();
                onUpgrade();
              }}
            >
              {state === 'upgradeable'
                ? `Upgrade to Pro £${String(row.upgrade_price)}`
                : 'Upgrade to Pro'}
            </button>
          )}
          {state === 'pending' && (
            <button
              type="button"
              role="menuitem"
              className="hi-ui-be-manage__item"
              disabled
              title={UPGRADE_PENDING_TOOLTIP}
            >
              Upgrade to Pro
            </button>
          )}
        </>
      )}
    </DropdownMenu>
  );
};
